import { useEffect, useState } from 'react';
import { bugReportsAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Bug, Search, Filter, MoreVertical, Clock, CheckCircle2, AlertTriangle,
  AlertCircle, Loader2, User, Calendar, Globe, Monitor, ChevronRight,
  MessageSquare, Trash2, Edit, Eye, RefreshCw, Save, Download, Copy,
  Archive, ArchiveRestore, Tag, Settings2
} from 'lucide-react';

const priorityConfig = {
  low: { label: 'Low', color: 'bg-slate-100 text-slate-700', icon: null },
  medium: { label: 'Medium', color: 'bg-blue-100 text-blue-700', icon: null },
  high: { label: 'High', color: 'bg-amber-100 text-amber-700', icon: AlertTriangle },
  critical: { label: 'Critical', color: 'bg-red-100 text-red-700', icon: AlertCircle }
};

const statusConfig = {
  new: { label: 'New', color: 'bg-purple-100 text-purple-700' },
  in_progress: { label: 'In Progress', color: 'bg-blue-100 text-blue-700' },
  resolved: { label: 'Resolved', color: 'bg-emerald-100 text-emerald-700' },
  closed: { label: 'Closed', color: 'bg-slate-100 text-slate-700' },
  wont_fix: { label: "Won't Fix", color: 'bg-slate-100 text-slate-600' }
};

export const BugReportCenterPage = () => {
  const { hasRole } = useAuth();
  const [bugs, setBugs] = useState([]);
  const [archivedBugs, setArchivedBugs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [selectedBug, setSelectedBug] = useState(null);
  const [detailDialog, setDetailDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [editFormData, setEditFormData] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [exportDialog, setExportDialog] = useState(false);
  const [exportedPrompt, setExportedPrompt] = useState(null);
  // Multi-select for export
  const [selectedBugIds, setSelectedBugIds] = useState([]);
  // Archive functionality
  const [activeTab, setActiveTab] = useState('active');
  const [archiveVersions, setArchiveVersions] = useState([]);
  const [currentVersion, setCurrentVersion] = useState('1.0');
  const [archiveDialog, setArchiveDialog] = useState(false);
  const [archiveVersion, setArchiveVersion] = useState('');
  const [versionFilter, setVersionFilter] = useState('all');

  useEffect(() => {
    fetchBugs();
    fetchStats();
    fetchArchiveData();
  }, []);

  const fetchBugs = async () => {
    setLoading(true);
    try {
      // Fetch active (non-resolved) bugs for the Active tab
      const res = await bugReportsAPI.getAll({ archived: false });
      // Filter out resolved bugs since they go to the Archive tab
      const activeBugs = (res.data || []).filter(bug => bug.status !== 'resolved');
      setBugs(activeBugs);
    } catch (error) {
      toast.error('Failed to load bug reports');
    } finally {
      setLoading(false);
    }
  };

  const fetchArchivedBugs = async (version = null) => {
    try {
      // Fetch resolved bugs as the archive
      const params = { status: 'resolved' };
      const res = await bugReportsAPI.getAll(params);
      setArchivedBugs(res.data || []);
    } catch (error) {
      toast.error('Failed to load archived bugs');
    }
  };

  const fetchArchiveData = async () => {
    try {
      const [versionsRes, currentRes] = await Promise.all([
        bugReportsAPI.getVersions(),
        bugReportsAPI.getCurrentVersion()
      ]);
      setArchiveVersions(versionsRes.data || []);
      setCurrentVersion(currentRes.data?.version || '1.0');
      setArchiveVersion(currentRes.data?.version || '1.0');
    } catch (error) {
      console.error('Failed to load archive data');
    }
  };

  const handleArchiveSelected = async () => {
    if (selectedBugIds.length === 0) {
      toast.error('Please select bugs to archive');
      return;
    }
    if (!archiveVersion.trim()) {
      toast.error('Please enter a version number');
      return;
    }
    
    try {
      await bugReportsAPI.archive(selectedBugIds, archiveVersion);
      toast.success(`Archived ${selectedBugIds.length} bugs under version ${archiveVersion}`);
      setSelectedBugIds([]);
      setArchiveDialog(false);
      fetchBugs();
      fetchArchiveData();
    } catch (error) {
      toast.error('Failed to archive bugs');
    }
  };

  const handleUnarchiveSelected = async () => {
    if (selectedBugIds.length === 0) {
      toast.error('Please select bugs to unarchive');
      return;
    }
    
    try {
      await bugReportsAPI.unarchive(selectedBugIds);
      toast.success(`Unarchived ${selectedBugIds.length} bugs`);
      setSelectedBugIds([]);
      fetchArchivedBugs(versionFilter);
      fetchBugs();
    } catch (error) {
      toast.error('Failed to unarchive bugs');
    }
  };

  const handleUpdateCurrentVersion = async (newVersion) => {
    try {
      await bugReportsAPI.updateCurrentVersion(newVersion);
      setCurrentVersion(newVersion);
      toast.success(`CRM version updated to ${newVersion}`);
    } catch (error) {
      toast.error('Failed to update version');
    }
  };

  useEffect(() => {
    if (activeTab === 'archived') {
      fetchArchivedBugs(versionFilter);
    }
  }, [activeTab, versionFilter]);

  const fetchStats = async () => {
    try {
      const res = await bugReportsAPI.getStats();
      setStats(res.data);
    } catch (error) {
      console.error('Failed to fetch stats');
    }
  };

  // Toggle bug selection
  const toggleBugSelection = (bugId) => {
    setSelectedBugIds(prev => 
      prev.includes(bugId) 
        ? prev.filter(id => id !== bugId)
        : [...prev, bugId]
    );
  };

  // Select all visible bugs
  const toggleSelectAll = () => {
    const visibleBugIds = filteredBugs.map(b => b.id);
    if (selectedBugIds.length === visibleBugIds.length) {
      setSelectedBugIds([]);
    } else {
      setSelectedBugIds(visibleBugIds);
    }
  };

  // Generate prompt for selected bugs
  const generatePromptForBugs = (bugsToExport) => {
    if (bugsToExport.length === 0) return '';
    
    if (bugsToExport.length === 1) {
      const bug = bugsToExport[0];
      let prompt = `## Bug Fix Request: ${bug.title}\n`;
      prompt += `**Priority:** ${(bug.priority || 'medium').toUpperCase()}\n`;
      prompt += `**Status:** ${bug.status}\n`;
      if (bug.page_url) prompt += `**Page URL:** ${bug.page_url}\n`;
      prompt += `\n### Problem Description\n${bug.description}\n`;
      if (bug.steps_to_reproduce) prompt += `\n### Steps to Reproduce\n${bug.steps_to_reproduce}\n`;
      if (bug.expected_behavior) prompt += `\n### Expected Behavior\n${bug.expected_behavior}\n`;
      if (bug.actual_behavior) prompt += `\n### Actual Behavior\n${bug.actual_behavior}\n`;
      if (bug.browser_info) prompt += `\n### Environment\n${bug.browser_info}\n`;
      prompt += `\n### Instructions\nPlease investigate and fix this bug. Test thoroughly after fixing.`;
      return prompt;
    }
    
    // Multiple bugs
    let prompt = `# Bug Fix Queue - ${bugsToExport.length} Issues\n\n`;
    prompt += `Please fix the following bugs in order of priority:\n\n`;
    prompt += `---\n\n`;
    
    bugsToExport.forEach((bug, index) => {
      prompt += `## ${index + 1}. ${bug.title} [${(bug.priority || 'medium').toUpperCase()}]\n`;
      if (bug.page_url) prompt += `**Page:** ${bug.page_url}\n`;
      prompt += `\n${bug.description}\n`;
      if (bug.steps_to_reproduce) prompt += `\n**Steps:** ${bug.steps_to_reproduce}\n`;
      if (bug.expected_behavior) prompt += `**Expected:** ${bug.expected_behavior}\n`;
      if (bug.actual_behavior) prompt += `**Actual:** ${bug.actual_behavior}\n`;
      prompt += `\n---\n\n`;
    });
    
    return prompt;
  };

  // Export selected bugs
  const handleExportSelected = () => {
    const bugsToExport = bugs.filter(b => selectedBugIds.includes(b.id));
    if (bugsToExport.length === 0) {
      toast.error('Please select at least one bug to export');
      return;
    }
    
    // Sort by priority
    const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
    bugsToExport.sort((a, b) => (priorityOrder[a.priority] || 2) - (priorityOrder[b.priority] || 2));
    
    const prompt = generatePromptForBugs(bugsToExport);
    setExportedPrompt({
      title: bugsToExport.length === 1 ? bugsToExport[0].title : `${bugsToExport.length} Selected Bugs`,
      prompt,
      count: bugsToExport.length
    });
    setExportDialog(true);
  };

  // Copy prompt to clipboard
  const handleCopyPrompt = () => {
    if (exportedPrompt?.prompt) {
      navigator.clipboard.writeText(exportedPrompt.prompt);
      toast.success('Copied! Paste into Emergent to fix.');
    }
  };

  const handleStatusChange = async (bugId, newStatus) => {
    setUpdating(true);
    try {
      await bugReportsAPI.update(bugId, { status: newStatus });
      toast.success('Bug status updated');
      fetchBugs();
      fetchStats();
      if (selectedBug?.id === bugId) {
        setSelectedBug(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const handlePriorityChange = async (bugId, newPriority) => {
    setUpdating(true);
    try {
      await bugReportsAPI.update(bugId, { priority: newPriority });
      toast.success('Bug priority updated');
      fetchBugs();
      if (selectedBug?.id === bugId) {
        setSelectedBug(prev => ({ ...prev, priority: newPriority }));
      }
    } catch (error) {
      toast.error('Failed to update priority');
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async (bugId) => {
    if (!window.confirm('Are you sure you want to delete this bug report?')) return;
    try {
      await bugReportsAPI.delete(bugId);
      toast.success('Bug report deleted');
      fetchBugs();
      fetchStats();
      setDetailDialog(false);
    } catch (error) {
      toast.error('Failed to delete bug report');
    }
  };

  const handleAddResolution = async (bugId, notes) => {
    setUpdating(true);
    try {
      await bugReportsAPI.update(bugId, { resolution_notes: notes, status: 'resolved' });
      toast.success('Resolution added');
      fetchBugs();
      fetchStats();
      if (selectedBug?.id === bugId) {
        setSelectedBug(prev => ({ ...prev, resolution_notes: notes, status: 'resolved' }));
      }
    } catch (error) {
      toast.error('Failed to add resolution');
    } finally {
      setUpdating(false);
    }
  };

  const openEditDialog = (bug) => {
    setEditFormData({
      title: bug.title || '',
      description: bug.description || '',
      steps_to_reproduce: bug.steps_to_reproduce || '',
      expected_behavior: bug.expected_behavior || '',
      actual_behavior: bug.actual_behavior || '',
      priority: bug.priority || 'medium',
      status: bug.status || 'new',
      resolution_notes: bug.resolution_notes || '',
      assigned_to: bug.assigned_to || ''
    });
    setSelectedBug(bug);
    setEditDialog(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedBug || !editFormData) return;
    setUpdating(true);
    try {
      await bugReportsAPI.update(selectedBug.id, editFormData);
      toast.success('Bug report updated successfully');
      fetchBugs();
      fetchStats();
      setEditDialog(false);
      setEditFormData(null);
    } catch (error) {
      toast.error('Failed to update bug report');
    } finally {
      setUpdating(false);
    }
  };

  const openBugDetail = (bug) => {
    setSelectedBug(bug);
    setDetailDialog(true);
  };

  const filteredBugs = bugs.filter(bug => {
    const matchesSearch = !searchTerm || 
      bug.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bug.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bug.reported_by_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || bug.status === statusFilter;
    const matchesPriority = priorityFilter === 'all' || bug.priority === priorityFilter;
    return matchesSearch && matchesStatus && matchesPriority;
  });

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  if (!hasRole('admin')) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold">Access Denied</h2>
          <p className="text-muted-foreground">Only administrators can access the Bug Report Center</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="bug-report-center">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Bug className="h-6 w-6 text-red-500" />
            Bug Report Center
          </h1>
          <p className="text-muted-foreground">Track and manage all reported bugs in the system</p>
        </div>
        <div className="flex gap-2">
          {selectedBugIds.length > 0 && (
            <Button onClick={handleExportSelected} className="bg-blue-600 hover:bg-blue-700" data-testid="export-selected-btn">
              <Download className="h-4 w-4 mr-2" />
              Export {selectedBugIds.length} Selected
            </Button>
          )}
          <Button onClick={fetchBugs} variant="outline" disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Selection Info Bar */}
      {selectedBugIds.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between">
          <span className="text-blue-700 font-medium">
            {selectedBugIds.length} bug{selectedBugIds.length > 1 ? 's' : ''} selected
          </span>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => setSelectedBugIds([])} className="text-blue-600">
              Clear Selection
            </Button>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total</p>
                  <p className="text-2xl font-bold">{stats.total}</p>
                </div>
                <Bug className="h-8 w-8 text-slate-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-purple-200 bg-purple-50/50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-600">New</p>
                  <p className="text-2xl font-bold text-purple-700">{stats.new}</p>
                </div>
                <AlertCircle className="h-8 w-8 text-purple-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-blue-200 bg-blue-50/50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600">In Progress</p>
                  <p className="text-2xl font-bold text-blue-700">{stats.in_progress}</p>
                </div>
                <Clock className="h-8 w-8 text-blue-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-emerald-200 bg-emerald-50/50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-emerald-600">Resolved</p>
                  <p className="text-2xl font-bold text-emerald-700">{stats.resolved}</p>
                </div>
                <CheckCircle2 className="h-8 w-8 text-emerald-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-red-200 bg-red-50/50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-red-600">Critical Open</p>
                  <p className="text-2xl font-bold text-red-700">{stats.critical_open}</p>
                </div>
                <AlertTriangle className="h-8 w-8 text-red-400" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs for Active/Archive */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="active" data-testid="active-bugs-tab">
            Active Bugs
          </TabsTrigger>
          <TabsTrigger value="archived" data-testid="archive-tab">
            <Archive className="h-4 w-4 mr-2" />
            Archive (Resolved)
          </TabsTrigger>
        </TabsList>

        <TabsContent value="active">
          {/* Filters */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search bugs by title, description, or reporter..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="bug-search-input"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[160px]" data-testid="status-filter">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                    <SelectItem value="wont_fix">Won&apos;t Fix</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                  <SelectTrigger className="w-[160px]" data-testid="priority-filter">
                    <SelectValue placeholder="Priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Priorities</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

      {/* Bug List (Active) */}
      <Card className="mt-4">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : filteredBugs.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40px]">
                    <Checkbox 
                      checked={selectedBugIds.length === filteredBugs.length && filteredBugs.length > 0}
                      onCheckedChange={toggleSelectAll}
                      data-testid="select-all-bugs"
                    />
                  </TableHead>
                  <TableHead className="w-[45%]">Bug Report</TableHead>
                  <TableHead>Reporter</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredBugs.map((bug) => (
                  <TableRow 
                    key={bug.id} 
                    className={`cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 ${selectedBugIds.includes(bug.id) ? 'bg-blue-50' : ''}`}
                    onClick={() => openBugDetail(bug)}
                    data-testid={`bug-row-${bug.id}`}
                  >
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox 
                        checked={selectedBugIds.includes(bug.id)}
                        onCheckedChange={() => toggleBugSelection(bug.id)}
                        data-testid={`select-bug-${bug.id}`}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          {bug.priority === 'critical' && (
                            <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
                          )}
                          <p className="font-medium line-clamp-1">{bug.title}</p>
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-1">
                          {bug.description}
                        </p>
                        {bug.page_url && (
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Globe className="h-3 w-3" />
                            {bug.page_url}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Avatar className="h-7 w-7">
                          <AvatarFallback className="text-xs bg-slate-200">
                            {getInitials(bug.reported_by_name)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-sm">{bug.reported_by_name || 'Unknown'}</span>
                      </div>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Select 
                        value={bug.priority} 
                        onValueChange={(v) => handlePriorityChange(bug.id, v)}
                      >
                        <SelectTrigger className="w-[100px] h-7">
                          <Badge className={`${priorityConfig[bug.priority]?.color} text-xs`}>
                            {priorityConfig[bug.priority]?.label}
                          </Badge>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                          <SelectItem value="critical">Critical</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Select 
                        value={bug.status} 
                        onValueChange={(v) => handleStatusChange(bug.id, v)}
                      >
                        <SelectTrigger className="w-[120px] h-7">
                          <Badge className={`${statusConfig[bug.status]?.color} text-xs`}>
                            {statusConfig[bug.status]?.label}
                          </Badge>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="in_progress">In Progress</SelectItem>
                          <SelectItem value="resolved">Resolved</SelectItem>
                          <SelectItem value="closed">Closed</SelectItem>
                          <SelectItem value="wont_fix">Won&apos;t Fix</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Calendar className="h-3 w-3" />
                        {bug.created_at ? format(new Date(bug.created_at), 'MMM d, yyyy') : '-'}
                      </div>
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openBugDetail(bug)}>
                            <Eye className="h-4 w-4 mr-2" /> View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openEditDialog(bug)}>
                            <Edit className="h-4 w-4 mr-2" /> Edit Bug
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => {
                              // Export single bug directly
                              const prompt = generatePromptForBugs([bug]);
                              setExportedPrompt({
                                title: bug.title,
                                priority: bug.priority,
                                prompt,
                                count: 1
                              });
                              setExportDialog(true);
                            }} 
                            data-testid={`export-bug-${bug.id}`}
                          >
                            <Download className="h-4 w-4 mr-2" /> Export to Emergent
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleDelete(bug.id)}
                            className="text-red-600"
                          >
                            <Trash2 className="h-4 w-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <CheckCircle2 className="h-12 w-12 mb-4" />
              <p className="text-lg font-medium">No bugs found</p>
              <p className="text-sm">
                {searchTerm || statusFilter !== 'all' || priorityFilter !== 'all' 
                  ? 'Try adjusting your filters' 
                  : 'All systems running smoothly!'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        {/* Archive Tab Content */}
        <TabsContent value="archived">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Archive className="h-5 w-5" />
                Resolved Bugs Archive
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Bugs that have been resolved and archived for reference
              </p>
            </CardHeader>
            <CardContent>
              {archivedBugs.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Bug Title</TableHead>
                      <TableHead>Priority</TableHead>
                      <TableHead>Resolved Date</TableHead>
                      <TableHead>Resolution Notes</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {archivedBugs.map(bug => (
                      <TableRow key={bug.id}>
                        <TableCell>
                          <div className="font-medium">{bug.title}</div>
                          <div className="text-xs text-muted-foreground truncate max-w-[300px]">
                            {bug.description}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={priorityConfig[bug.priority]?.color}>
                            {priorityConfig[bug.priority]?.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {bug.updated_at ? format(new Date(bug.updated_at), 'MMM d, yyyy') : '-'}
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate text-sm">
                          {bug.resolution_notes || 'No notes'}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openBugDetail(bug)}
                          >
                            View Details
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="flex flex-col items-center justify-center h-48 text-muted-foreground">
                  <Archive className="h-12 w-12 mb-4" />
                  <p className="text-lg font-medium">No archived bugs</p>
                  <p className="text-sm">Resolved bugs will appear here</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Bug Detail Dialog */}
      <Dialog open={detailDialog} onOpenChange={setDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh]">
          {selectedBug && (
            <>
              <DialogHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${
                      selectedBug.priority === 'critical' ? 'bg-red-100' :
                      selectedBug.priority === 'high' ? 'bg-amber-100' :
                      'bg-slate-100'
                    }`}>
                      <Bug className={`h-5 w-5 ${
                        selectedBug.priority === 'critical' ? 'text-red-600' :
                        selectedBug.priority === 'high' ? 'text-amber-600' :
                        'text-slate-600'
                      }`} />
                    </div>
                    <div>
                      <DialogTitle className="text-xl">{selectedBug.title}</DialogTitle>
                      <DialogDescription className="flex items-center gap-2 mt-1">
                        <Badge className={priorityConfig[selectedBug.priority]?.color}>
                          {priorityConfig[selectedBug.priority]?.label} Priority
                        </Badge>
                        <Badge className={statusConfig[selectedBug.status]?.color}>
                          {statusConfig[selectedBug.status]?.label}
                        </Badge>
                      </DialogDescription>
                    </div>
                  </div>
                </div>
              </DialogHeader>
              
              <div className="space-y-6 max-h-[60vh] overflow-y-auto pr-2">
                {/* Reporter Info */}
                <div className="flex items-center gap-4 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <Avatar className="h-10 w-10">
                    <AvatarFallback>{getInitials(selectedBug.reported_by_name)}</AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">{selectedBug.reported_by_name || 'Unknown User'}</p>
                    <p className="text-sm text-muted-foreground">
                      Reported on {selectedBug.created_at ? format(new Date(selectedBug.created_at), 'MMMM d, yyyy \'at\' h:mm a') : 'Unknown date'}
                    </p>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Description</Label>
                  <p className="mt-1 text-sm whitespace-pre-wrap">{selectedBug.description}</p>
                </div>

                {/* Steps to Reproduce */}
                {selectedBug.steps_to_reproduce && (
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Steps to Reproduce</Label>
                    <div className="mt-1 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <p className="text-sm whitespace-pre-wrap">{selectedBug.steps_to_reproduce}</p>
                    </div>
                  </div>
                )}

                {/* Expected vs Actual */}
                {(selectedBug.expected_behavior || selectedBug.actual_behavior) && (
                  <div className="grid grid-cols-2 gap-4">
                    {selectedBug.expected_behavior && (
                      <div>
                        <Label className="text-sm font-medium text-emerald-600">Expected Behavior</Label>
                        <p className="mt-1 text-sm p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
                          {selectedBug.expected_behavior}
                        </p>
                      </div>
                    )}
                    {selectedBug.actual_behavior && (
                      <div>
                        <Label className="text-sm font-medium text-red-600">Actual Behavior</Label>
                        <p className="mt-1 text-sm p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                          {selectedBug.actual_behavior}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Technical Info */}
                <div className="grid grid-cols-2 gap-4">
                  {selectedBug.page_url && (
                    <div>
                      <Label className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                        <Globe className="h-3 w-3" /> Page URL
                      </Label>
                      <p className="mt-1 text-sm font-mono bg-slate-100 dark:bg-slate-800 p-2 rounded">
                        {selectedBug.page_url}
                      </p>
                    </div>
                  )}
                  {selectedBug.browser_info && (
                    <div>
                      <Label className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                        <Monitor className="h-3 w-3" /> Browser
                      </Label>
                      <p className="mt-1 text-xs text-muted-foreground bg-slate-100 dark:bg-slate-800 p-2 rounded truncate">
                        {selectedBug.browser_info}
                      </p>
                    </div>
                  )}
                </div>

                {/* Resolution Notes */}
                {selectedBug.resolution_notes && (
                  <div>
                    <Label className="text-sm font-medium text-emerald-600 flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3" /> Resolution
                    </Label>
                    <div className="mt-1 p-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg">
                      <p className="text-sm whitespace-pre-wrap">{selectedBug.resolution_notes}</p>
                      {selectedBug.resolved_at && (
                        <p className="text-xs text-muted-foreground mt-2">
                          Resolved on {format(new Date(selectedBug.resolved_at), 'MMMM d, yyyy')}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Quick Actions */}
                {!['resolved', 'closed', 'wont_fix'].includes(selectedBug.status) && (
                  <div className="border-t pt-4">
                    <Label className="text-sm font-medium mb-2 block">Quick Actions</Label>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleStatusChange(selectedBug.id, 'in_progress')}
                        disabled={updating || selectedBug.status === 'in_progress'}
                      >
                        <Clock className="h-4 w-4 mr-1" /> Mark In Progress
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                        onClick={() => {
                          const notes = window.prompt('Enter resolution notes:');
                          if (notes) handleAddResolution(selectedBug.id, notes);
                        }}
                        disabled={updating}
                      >
                        <CheckCircle2 className="h-4 w-4 mr-1" /> Resolve
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="text-slate-600"
                        onClick={() => handleStatusChange(selectedBug.id, 'wont_fix')}
                        disabled={updating}
                      >
                        Won&apos;t Fix
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setDetailDialog(false)}>
                  Close
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => {
                    setDetailDialog(false);
                    openEditDialog(selectedBug);
                  }}
                >
                  <Edit className="h-4 w-4 mr-2" /> Edit
                </Button>
                <Button 
                  variant="destructive" 
                  onClick={() => handleDelete(selectedBug.id)}
                >
                  <Trash2 className="h-4 w-4 mr-2" /> Delete
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Bug Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5 text-blue-500" />
              Edit Bug Report
            </DialogTitle>
            <DialogDescription>
              Update the bug report details below
            </DialogDescription>
          </DialogHeader>
          
          {editFormData && (
            <ScrollArea className="max-h-[60vh] pr-4">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-title">Title</Label>
                  <Input
                    id="edit-title"
                    value={editFormData.title}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Bug title"
                    data-testid="edit-bug-title"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="edit-description">Description</Label>
                  <Textarea
                    id="edit-description"
                    value={editFormData.description}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Detailed description of the bug"
                    rows={3}
                    data-testid="edit-bug-description"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="edit-steps">Steps to Reproduce</Label>
                  <Textarea
                    id="edit-steps"
                    value={editFormData.steps_to_reproduce}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, steps_to_reproduce: e.target.value }))}
                    placeholder="1. Go to...&#10;2. Click on...&#10;3. See error"
                    rows={3}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-expected">Expected Behavior</Label>
                    <Textarea
                      id="edit-expected"
                      value={editFormData.expected_behavior}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, expected_behavior: e.target.value }))}
                      placeholder="What should happen"
                      rows={2}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-actual">Actual Behavior</Label>
                    <Textarea
                      id="edit-actual"
                      value={editFormData.actual_behavior}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, actual_behavior: e.target.value }))}
                      placeholder="What actually happens"
                      rows={2}
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-priority">Priority</Label>
                    <Select 
                      value={editFormData.priority} 
                      onValueChange={(v) => setEditFormData(prev => ({ ...prev, priority: v }))}
                    >
                      <SelectTrigger data-testid="edit-bug-priority">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="critical">Critical</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-status">Status</Label>
                    <Select 
                      value={editFormData.status} 
                      onValueChange={(v) => setEditFormData(prev => ({ ...prev, status: v }))}
                    >
                      <SelectTrigger data-testid="edit-bug-status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new">New</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="resolved">Resolved</SelectItem>
                        <SelectItem value="closed">Closed</SelectItem>
                        <SelectItem value="wont_fix">Won&apos;t Fix</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="edit-resolution">Resolution Notes</Label>
                  <Textarea
                    id="edit-resolution"
                    value={editFormData.resolution_notes}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, resolution_notes: e.target.value }))}
                    placeholder="Notes about how this bug was fixed"
                    rows={2}
                  />
                </div>
              </div>
            </ScrollArea>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} disabled={updating} data-testid="save-bug-edit-btn">
              {updating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export Prompt Dialog */}
      <Dialog open={exportDialog} onOpenChange={setExportDialog}>
        <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="h-5 w-5 text-blue-600" />
              Export to Emergent
            </DialogTitle>
            <DialogDescription>
              {exportedPrompt?.count > 1 
                ? `${exportedPrompt.count} bugs ready to export. Copy and paste into Emergent.`
                : 'Copy this prompt and paste it into a new Emergent chat.'
              }
            </DialogDescription>
          </DialogHeader>
          
          {exportedPrompt && (
            <div className="flex-1 flex flex-col gap-4 min-h-0">
              {/* Header with copy button */}
              <div className="flex items-center justify-between flex-shrink-0">
                <div>
                  <h3 className="font-semibold text-lg">{exportedPrompt.title}</h3>
                  {exportedPrompt.priority && (
                    <Badge className={priorityConfig[exportedPrompt.priority]?.color}>
                      {exportedPrompt.priority}
                    </Badge>
                  )}
                </div>
                <Button onClick={handleCopyPrompt} size="lg" className="bg-blue-600 hover:bg-blue-700" data-testid="copy-prompt-btn">
                  <Copy className="h-4 w-4 mr-2" />
                  Copy to Clipboard
                </Button>
              </div>
              
              {/* Scrollable prompt area - fixed height */}
              <div className="flex-1 min-h-0 border rounded-lg bg-slate-900 overflow-hidden">
                <div className="h-full overflow-y-auto p-4">
                  <pre className="text-sm text-slate-100 whitespace-pre-wrap font-mono leading-relaxed">
                    {exportedPrompt.prompt}
                  </pre>
                </div>
              </div>
              
              {/* Instructions */}
              <div className="flex-shrink-0 text-sm text-muted-foreground bg-amber-50 p-3 rounded-lg border border-amber-200">
                <p className="font-medium text-amber-700 mb-1">Quick Steps:</p>
                <p className="text-amber-600">
                  1. Click &quot;Copy to Clipboard&quot; → 2. Open new Emergent chat → 3. Paste &amp; Enter
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter className="flex-shrink-0">
            <Button variant="outline" onClick={() => setExportDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BugReportCenterPage;
